import { useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import { FeedAttributionConfig } from './config'

// Dedup for broken mark assets — onError fires on every mount, a grid
// renders many cards, and the detail page renders the same asset, so a
// single module-level Set (shared by every consumer) collapses those to one
// report per src rather than one per surface.
//
// Scope note: this Set lives as long as the loaded module instance, NOT a
// page. In the browser that means one full page load, surviving every
// client-side route change; in an SSR/Lambda process it is per warm
// instance, shared across every request that instance serves. So "reports
// once" means once per process, not once per page view or per user — a
// silenced report is expected on the second request a warm instance handles.
const reportedMarks = new Set<string>()

/**
 * The MLS-supplied mark <img> (Triad 18.2 (F)/(G)/(H); MLS GRID Rule 23)
 * with the shared decorative-alt / hide-on-error / report-once behavior.
 * Renders nothing when the config carries no markSrc. WHERE the mark is
 * required, and which src it is, stay call-site decisions: ListingCard's
 * photo Figure gates on `config.thumbnailIcon` and passes the self-hosted
 * card asset, MlsAttribution passes the payload's per-feed CDN logo.
 */
export function MlsMark({
  config,
  feedName,
  className,
  width = config.markWidth,
  height = config.markHeight,
}: {
  config: Pick<FeedAttributionConfig, 'markSrc' | 'markWidth' | 'markHeight'>
  feedName: string
  className?: string
  /**
   * Per-surface render size, defaulting to the config's detail-page size
   * (half the asset's pixel dimensions, 2x). The card badge passes the
   * config's thumbnail size instead — see markSrc's docblock in config.ts.
   */
  width?: number
  height?: number
}) {
  // Hide-on-error is state, not a DOM mutation: React owns this tree, and
  // the listings grid is index-keyed, so a mutated inline style survives
  // subtree reuse and would permanently eat the REQUIRED mark at that grid
  // position. Keyed on the failed src so a different asset rendered by the
  // same mount is unaffected.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const { markSrc } = config

  if (!markSrc || failedSrc === markSrc) {
    return null
  }

  return (
    /* eslint-disable-next-line @next/next/no-img-element */
    <img
      src={markSrc}
      width={width}
      height={height}
      alt=""
      className={className}
      onError={() => {
        // The mark is REQUIRED display — hiding keeps the layout clean, but
        // a broken asset is silent non-compliance, so make it observable
        // (once per src).
        if (!reportedMarks.has(markSrc)) {
          reportedMarks.add(markSrc)
          Sentry.captureMessage(
            `MLS mark failed to load: ${markSrc} (${feedName})`,
            { level: 'warning', tags: { feed: feedName } }
          )
        }
        setFailedSrc(markSrc)
      }}
    />
  )
}
